import { useMutation, useQuery } from "@tanstack/react-query";
import { enrollmentService } from "@/services/enrollments.service";
import {
  RoleSubType,
  SearchRedeemRequestsPayload,
  UpdateModulesPayload,
  UpdateProviderModulesPayload,
  CreateEnrollment,
  DisenrollParams,
} from "@/domain/enrollment.interface";
import { TestResultStatusEnum } from "@/domain/enrollment.enum";

export const useListEnrollments = (
  participantId: string,
  userId: string,
  enabled = false,
) => {
  const {
    data: enrollments,
    isLoading: isLoadingEnrollments,
    isError: isErrorEnrollments,
    refetch: refetchEnrollments,
  } = useQuery({
    queryKey: ["get-enrollments", participantId, userId],
    queryFn: () => enrollmentService.list(participantId, userId),
    staleTime: 1 * 60 * 60 * 1000,
    enabled,
  });

  return {
    enrollments,
    isLoadingEnrollments,
    isErrorEnrollments,
    refetchEnrollments,
  };
};

export const useCreateEnrollment = () => {
  return useMutation({
    mutationFn: (data: CreateEnrollment) =>
      enrollmentService.createEnrollment(data),
  });
};

export const useDisenroll = () => {
  return useMutation({
    mutationFn: async (params: DisenrollParams) => {
      await enrollmentService.disenroll(params);
    },
  });
};

export const useGetEnrollment = (enrollmentId: string, enabled = false) => {
  const {
    data: enrollment,
    isLoading: isLoadingEnrollment,
    isError: isErrorEnrollment,
    refetch: refetchEnrollment,
  } = useQuery({
    queryKey: ["get-enrollment", enrollmentId],
    queryFn: () => enrollmentService.findById(enrollmentId),
    staleTime: 1 * 60 * 60 * 1000,
    enabled,
  });

  return {
    enrollment,
    isLoadingEnrollment,
    isErrorEnrollment,
    refetchEnrollment,
  };
};

interface UpdateEnrollmentPayload {
  accountId: string;
  participantId: string;
  enrollmentId: string;
  enrollmentData: {
    programId: string;
    orgUnitId: string;
    cmCoordinator: string;
    additionalStaff: string[];
    notificationWindow?: {
      from: string;
      to: string;
    };
    updatedBy: string;
    formFactor1?: string | null;
    formFactor2?: string | null;
    naloxoneId?: string | null;
  };
}

export const useUpdateEnrollment = () => {
  return useMutation({
    mutationFn: async ({
      accountId,
      participantId,
      enrollmentId,
      enrollmentData,
    }: UpdateEnrollmentPayload) => {
      await enrollmentService.updateEnrollment(
        accountId,
        participantId,
        enrollmentId,
        enrollmentData,
      );
    },
  });
};

export const useUpdateProviderModules = () => {
  return useMutation({
    mutationFn: async ({
      enrollmentId,
      providerId,
      modulesIds,
    }: UpdateProviderModulesPayload) => {
      await enrollmentService.updateProviderModules(
        enrollmentId,
        providerId,
        modulesIds,
      );
    },
  });
};

export const useUpdateSupportStaffModules = () => {
  return useMutation({
    mutationFn: async ({
      enrollmentId,
      providerId,
      modulesIds,
      providerType,
    }: UpdateModulesPayload) => {
      await enrollmentService.updateSupportStaffModules(
        enrollmentId,
        providerId,
        modulesIds,
        providerType,
      );
    },
  });
};

export const useRemoveSupportStaff = () => {
  return useMutation({
    mutationFn: async ({
      enrollmentId,
      providerId,
      providerType,
    }: {
      enrollmentId: string;
      providerId: string;
      providerType: RoleSubType;
    }) => {
      await enrollmentService.removeSupportStaff(
        enrollmentId,
        providerId,
        providerType,
      );
    },
  });
};

export const useUpdateAdditionalOfficers = () => {
  return useMutation({
    mutationFn: async ({
      enrollmentId,
      additionalOfficerIds,
    }: {
      enrollmentId: string;
      additionalOfficerIds: string[];
    }) => {
      await enrollmentService.updateAdditionalOfficers(
        enrollmentId,
        additionalOfficerIds,
      );
    },
  });
};

export const useListRedeemRequests = (
  enrollmentId: string,
  enabled = false,
) => {
  const {
    data: redeemRequests,
    isLoading: isLoadingRedeemRequests,
    isError: isErrorRedeemRequests,
    refetch: refetchRedeemRequests,
  } = useQuery({
    queryKey: ["list-redeem-requests", enrollmentId],
    queryFn: () => enrollmentService.listRedeemRequests(enrollmentId),
    enabled,
  });

  return {
    redeemRequests,
    isLoadingRedeemRequests,
    isErrorRedeemRequests,
    refetchRedeemRequests,
  };
};

export const useGetEnrollmentBalance = (
  enrollmentId: string,
  enabled = false,
) => {
  const {
    data: enrollmentBalance,
    isLoading: isLoadingEnrollmentBalance,
    isError: isErrorEnrollmentBalance,
    refetch: refetchEnrollmentBalance,
  } = useQuery({
    queryKey: ["get-enrollment-balance", enrollmentId],
    queryFn: () => enrollmentService.getEnrollmentBalanceSummary(enrollmentId),
    enabled,
  });

  return {
    enrollmentBalance,
    isLoadingEnrollmentBalance,
    isErrorEnrollmentBalance,
    refetchEnrollmentBalance,
  };
};

export const useMarkRedeemRequestViewed = () => {
  return useMutation({
    mutationFn: async ({
      enrollmentId,
      ids,
    }: {
      enrollmentId: string;
      ids: string[];
    }) =>
      await enrollmentService.updateRedeemRequestIsViewed(enrollmentId, ids),
  });
};

export const useApproveRedeemRequest = () => {
  return useMutation({
    mutationFn: async ({
      enrollmentId,
      redeemRequestId,
      approvedBy,
    }: {
      enrollmentId: string;
      redeemRequestId: string;
      approvedBy: string;
    }) =>
      await enrollmentService.approveRedeemRequest(
        enrollmentId,
        redeemRequestId,
        approvedBy,
      ),
  });
};

export const useRejectRedeemRequest = () => {
  return useMutation({
    mutationFn: async ({
      enrollmentId,
      redeemRequestId,
      rejectedBy,
      rejectedComment,
    }: {
      enrollmentId: string;
      redeemRequestId: string;
      rejectedBy: string;
      rejectedComment: string;
    }) =>
      await enrollmentService.rejectRedeemRequest(
        enrollmentId,
        redeemRequestId,
        rejectedBy,
        rejectedComment,
      ),
  });
};

export const useSearchRedeemRequests = ({
  sortField,
  sortOrder,
  pageNumber,
  pageSize,
  status,
  enrollmentId,
}: SearchRedeemRequestsPayload) => {
  const {
    data: redeemRequests,
    isLoading: isLoadingRedeemRequests,
    isFetching: isFetchingRedeemRequests,
    refetch: refetchRedeemRequests,
  } = useQuery({
    queryKey: ["coyn-redeem-requests-search", enrollmentId],
    queryFn: () =>
      enrollmentService.searchRedeemRequests({
        sortField,
        sortOrder,
        pageNumber,
        pageSize,
        status,
        enrollmentId,
      }),
    enabled: enrollmentId !== "",
  });

  return {
    redeemRequests,
    isLoadingRedeemRequests,
    isFetchingRedeemRequests,
    refetchRedeemRequests,
  };
};

export const useAddCasePlan = () => {
  return useMutation({
    mutationFn: async ({
      enrollmentId,
      file,
    }: {
      enrollmentId: string;
      file: File;
    }) => await enrollmentService.addCasePlan(enrollmentId, file),
  });
};

export const useGetPlans = (enrollmentId: string) => {
  const {
    data: casePlans,
    isLoading: isLoadingCasePlans,
    isError: isErrorCasePlans,
    refetch: refetchCasePlans,
  } = useQuery({
    queryKey: ["get-case-plans", enrollmentId],
    queryFn: () => enrollmentService.getCasePlans(enrollmentId),
  });

  return {
    casePlans,
    isLoadingCasePlans,
    isErrorCasePlans,
    refetchCasePlans,
  };
};

export const useRemoveCasePlan = () => {
  return useMutation({
    mutationFn: async ({
      enrollmentId,
      casePlanId,
    }: {
      enrollmentId: string;
      casePlanId: string;
    }) => {
      await enrollmentService.removeCasePlan(enrollmentId, casePlanId);
    },
  });
};

export const useSignedLinks = ({
  enrollmentId,
  keys,
}: {
  enrollmentId: string;
  keys: string[];
}) => {
  const {
    data: signedLinks,
    isLoading: isLoadingSignedLinks,
    isError: isErrorSignedLinks,
    refetch: refetchSignedLinks,
  } = useQuery({
    queryKey: ["coyn-signed-links", enrollmentId, ...keys],
    queryFn: () => enrollmentService.getSignedLinks(enrollmentId, keys),
    enabled: !!enrollmentId && keys?.length > 0,
  });
  return {
    signedLinks,
    isLoadingSignedLinks,
    isErrorSignedLinks,
    refetchSignedLinks,
  };
};

export const useGetTestResults = ({
  enrollmentId,
  pageNumber,
  pageSize,
  sortField,
  sortOrder,
  enabled = true, // AC02: Allow conditional fetching
}: {
  enrollmentId: string;
  pageNumber: number;
  pageSize: number;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  enabled?: boolean;
}) => {
  const {
    data: testResults,
    isLoading: isLoadingTestResults,
    isError: isErrorTestResults,
    refetch: refetchTestResults,
  } = useQuery({
    queryKey: [
      "coyn-test-results",
      enrollmentId,
      pageNumber,
      pageSize,
      sortField,
      sortOrder,
    ],
    queryFn: () =>
      enrollmentService.getTestResults(
        enrollmentId,
        pageNumber,
        pageSize,
        sortField,
        sortOrder,
      ),
    enabled: enabled, // AC02: Only fetch when enabled
  });

  return {
    testResults,
    isLoadingTestResults,
    isErrorTestResults,
    refetchTestResults,
  };
};

export const useGetTestResult = (
  enrollmentId: string,
  testResultId: string,
) => {
  const {
    data: testResult,
    isLoading: isLoadingTestResult,
    isError: isErrorTestResult,
    refetch: refetchTestResult,
  } = useQuery({
    queryKey: ["coyn-test-result", enrollmentId, testResultId],
    queryFn: () => enrollmentService.getTestResult(enrollmentId, testResultId),
  });

  return {
    testResult,
    isLoadingTestResult,
    isErrorTestResult,
    refetchTestResult,
  };
};

export const useUpdateTestResultStatus = () => {
  return useMutation({
    mutationFn: async ({
      enrollmentId,
      testResultId,
      status,
      userId,
    }: {
      enrollmentId: string;
      testResultId: string;
      status: TestResultStatusEnum;
      userId: string;
    }) =>
      enrollmentService.updateTestResultStatus(
        enrollmentId,
        testResultId,
        status,
        userId,
      ),
  });
};

export const useCreateOnDemandIncentive = () => {
  return useMutation({
    mutationFn: async ({
      participantId,
      enrollmentId,
      amount,
      createdBy,
    }: {
      participantId: string;
      enrollmentId: string;
      amount: number;
      createdBy: string;
    }) =>
      enrollmentService.createOnDemandIncentive(
        enrollmentId,
        amount,
        createdBy,
        participantId,
      ),
  });
};
